import React, { FC, useState, useMemo, useCallback } from 'react'
import {
  Box,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import EditNoteIcon from '@mui/icons-material/EditNote'
import MicIcon from '@mui/icons-material/Mic'
import MedicationIcon from '@mui/icons-material/Medication'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  ConversationSegment,
  YakurekiDetail,
} from '@/shared/types/yakurekiCore'
import { BREAKPOINTS, RESPONSIVE_HEIGHTS } from '@/shared/constants/responsive'
import { GuidanceSupportData } from '@/apps/legacy/features/guidance/types/guidanceTypes'
import GuidanceScriptTabReadOnly from '@/apps/legacy/components/molecules/GuidanceScriptTabReadOnly'
import GuidanceDetailTab from '@/apps/legacy/components/molecules/GuidanceDetailTab'
import {
  Action,
  ActionStatus,
} from '@/features/patient-actions/types/patientActions'
import { TAB_INDICES } from '@/constants/patientConstants'
import ActionCardCompact from '@/apps/legacy/components/molecules/ActionCardCompact'
import TranscriptionViewer from '@/apps/legacy/components/molecules/TranscriptionViewer'
import { SoapImprovementCard } from '@/apps/legacy/components/molecules/SoapImprovementCard'
import { ActionCategorySection } from '@/apps/legacy/components/molecules/ActionCategorySection'
import {
  SoapImprovementSuggestion,
  SOAP_IMPROVEMENT_CATEGORIES,
} from '@/apps/legacy/features/formatter/types/soapImprovement'
import { useSOAPImprovement } from '@/features/formatter'
import AudioPlayer from '@/apps/legacy/components/molecules/AudioPlayer'
import PrescriptionInfoTab from '@/components/molecules/PrescriptionInfoTab'
import MemoTab from '@/apps/legacy/components/molecules/MemoTab'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { useUpdateMemoMutation } from '@/features/yakureki/hooks/queries/useUpdateMemoMutation'

interface PatientActionProposalProps {
  hasFollowupItems?: boolean
  onRegisterFollowup?: () => void
  dictatedData?: ConversationSegment[]
  audioUrl?: string
  sx?: object
  guidanceData?: GuidanceSupportData | null
  guidanceLoading?: boolean
  actions?: Action[]
  onStatusUpdate?: (id: string, status: ActionStatus) => void
  onOpenPatientDetail?: () => void
  onSoapImprovementExecute?: (prompt: string) => void
  yakurekiId?: string
  onSoapUpdate?: (updatedSoap: unknown) => void
  patientDetail?: YakurekiDetail
}

// ガイダンスタブコンテンツの共通コンポーネント
interface GuidanceTabContentProps {
  loading: boolean
  data: GuidanceSupportData | null
  type: 'script' | 'detail'
  emptyMessage: string
}

const GuidanceTabContent: FC<GuidanceTabContentProps> = ({
  loading,
  data,
  type,
  emptyMessage,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={40} />
      </Box>
    )
  }

  if (!data) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          backgroundColor: colorTokens.background.subtle,
          border: `1px solid ${colorTokens.border.light}`,
          borderRadius: 2,
          textAlign: 'center',
          minHeight: 120,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: colorTokens.text.secondary,
          }}
        >
          {emptyMessage}
        </Typography>
      </Paper>
    )
  }

  return type === 'script' ? (
    <GuidanceScriptTabReadOnly scriptData={data.scriptData} />
  ) : (
    <GuidanceDetailTab detailData={data.detailData} />
  )
}

const PatientActionProposal: FC<PatientActionProposalProps> = ({
  dictatedData = [],
  audioUrl,
  sx,
  guidanceData = null,
  guidanceLoading = false,
  actions = [],
  onStatusUpdate,
  onSoapImprovementExecute,
  yakurekiId,
  onSoapUpdate,
  patientDetail,
}) => {
  const memoItems = patientDetail?.memo_items ?? []
  const conversationSummary =
    patientDetail?.dictated?.conversation_summary ?? []
  const [tabValue, setTabValue] = useState<number>(TAB_INDICES.BASIC_RECORD)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)
  const updateMemoMutation = useUpdateMemoMutation()

  // タブの値を実際のタブインデックスにマッピング
  // Tabsコンポーネントには0-3の値のみ渡し、内部的には0-5の値を管理
  const getDisplayTabValue = () => {
    // 準備フレーズ(4)と準備詳細(5)の場合は、表示上は非選択状態にする
    if (
      tabValue === TAB_INDICES.GUIDANCE_SCRIPT ||
      tabValue === TAB_INDICES.GUIDANCE_DETAIL
    ) {
      return false // Tabsコンポーネントのvalue propにfalseを渡すと、どのタブも選択されない
    }
    return tabValue
  }

  const handleUpdateMemo = useCallback(
    (items: string[]) => {
      if (yakurekiId) {
        updateMemoMutation.mutate({
          yakurekiId,
          memoItems: items,
        })
      }
    },
    [yakurekiId, updateMemoMutation]
  )

  // カスタムフックを使用してSOAP改善提案を取得
  const {
    suggestions: soapImprovements,
    isLoading: soapImprovementsLoading,
    isPolling,
    applySuggestion,
    dismissSuggestion,
  } = useSOAPImprovement(yakurekiId)

  // 実際のactionsを使用
  const displayActions = actions

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // Tabsコンポーネントから来る値は0-2のみ
    setTabValue(newValue)
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = (index: number) => {
    setTabValue(index)
    handleMenuClose()
  }

  // SOAP改善提案をカテゴリ別にグループ化
  const groupedSoapImprovements = useMemo(() => {
    return SOAP_IMPROVEMENT_CATEGORIES.map((category) => ({
      ...category,
      suggestions: soapImprovements.filter(
        (suggestion) => suggestion.category === category.id
      ),
    }))
  }, [soapImprovements])

  // 改善提案の実行
  const handleSoapImprovementExecute = useCallback(
    async (suggestion: SoapImprovementSuggestion) => {
      const result = await applySuggestion(suggestion.id)

      if (result) {
        if (result.success && result.result?.updatedSoap) {
          // SOAP更新を親コンポーネントに通知
          if (onSoapUpdate) {
            onSoapUpdate(result.result.updatedSoap)
          }
        } else if (
          !result.success &&
          suggestion.chatPrompt &&
          onSoapImprovementExecute
        ) {
          // 手動更新が必要な場合はチャットを開く
          onSoapImprovementExecute(suggestion.chatPrompt)
        }
      }
    },
    [applySuggestion, onSoapUpdate, onSoapImprovementExecute]
  )

  // 改善提案の却下
  const handleSoapImprovementDismiss = useCallback(
    async (suggestionId: string) => {
      await dismissSuggestion(suggestionId)
    },
    [dismissSuggestion]
  )

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: colorTokens.background.paper,
        borderRadius: 2,
        border: `1px solid ${colorTokens.border.light}`,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        [`@media (max-width: ${BREAKPOINTS.MOBILE}px)`]: {
          height: 'auto',
          minHeight: `${RESPONSIVE_HEIGHTS.CHAT_AREA_MAX}px`,
          overflow: 'visible',
        },
        ...sx,
      }}
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Tabs
          value={getDisplayTabValue()}
          onChange={handleTabChange}
          sx={{
            flex: 1,
            minHeight: 48,
            '& .MuiTab-root': {
              minHeight: 48,
              color: colorTokens.grey[600],
              opacity: 1,
              '&.Mui-selected': {
                color: colorTokens.grey[800],
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: colorTokens.grey[800],
            },
          }}
        >
          <Tab
            icon={<EditNoteIcon sx={{ fontSize: 18, mr: 0.5 }} />}
            iconPosition="start"
            label="基本記録"
          />
          <Tab
            icon={<MicIcon sx={{ fontSize: 18, mr: 0.5 }} />}
            iconPosition="start"
            label="文字起こし"
          />
          <Tab
            icon={<MedicationIcon sx={{ fontSize: 18, mr: 0.5 }} />}
            iconPosition="start"
            label="処方情報"
          />
          <Tab
            icon={<AutoAwesomeIcon sx={{ fontSize: 18, mr: 0.5 }} />}
            iconPosition="start"
            label="ヒント"
          />
        </Tabs>
        <IconButton
          onClick={handleMenuClick}
          sx={{
            ml: 'auto',
            color: colorTokens.grey[800],
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 200,
                mt: 0.5,
                borderRadius: 2,
              },
            },
          }}
        >
          <MenuItem
            onClick={() => handleMenuItemClick(TAB_INDICES.GUIDANCE_SCRIPT)}
            selected={tabValue === TAB_INDICES.GUIDANCE_SCRIPT}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon>
              <TextSnippetIcon
                fontSize="small"
                sx={{ color: colorTokens.grey[800] }}
              />
            </ListItemIcon>
            <ListItemText primary="準備フレーズ" />
          </MenuItem>
          <MenuItem
            onClick={() => handleMenuItemClick(TAB_INDICES.GUIDANCE_DETAIL)}
            selected={tabValue === TAB_INDICES.GUIDANCE_DETAIL}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon>
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ color: colorTokens.grey[800] }}
              />
            </ListItemIcon>
            <ListItemText primary="準備詳細" />
          </MenuItem>
        </Menu>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          p: 3,
          minHeight: 0, // flexboxの子要素の最小高さをリセット
          [`@media (max-width: ${BREAKPOINTS.MOBILE}px)`]: {
            overflow: 'visible',
          },
        }}
      >
        {tabValue === TAB_INDICES.BASIC_RECORD && (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            {/* メモ */}
            <MemoTab
              title="メモ"
              memoItems={memoItems}
              onUpdateMemo={yakurekiId ? handleUpdateMemo : undefined}
              isUpdating={updateMemoMutation.isPending}
            />
            <Divider sx={{ my: 2 }} />

            {/* 会話要点 (AIまとめ) */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: colorTokens.grey[700],
                  mb: 0.5,
                }}
              >
                会話要点 (AIまとめ)
              </Typography>
              {conversationSummary.length > 0 ? (
                <Box>
                  {conversationSummary.map((item, index) => (
                    <Typography
                      key={index}
                      sx={{
                        color: colorTokens.text.primary,
                        py: 0.25,
                      }}
                    >
                      ・{item}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: colorTokens.text.secondary,
                  }}
                >
                  会話データはありません
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* 音声 */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: colorTokens.grey[700],
                  mb: 0.5,
                }}
              >
                音声
              </Typography>
              <AudioPlayer audioUrl={audioUrl} />
            </Box>
          </Box>
        )}

        {tabValue === TAB_INDICES.TRANSCRIPTION && (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {(patientDetail?.transcription?.post_batch_status === 'pending' ||
              patientDetail?.transcription?.post_batch_status ===
                'running') && (
              <Box
                sx={{
                  px: 1,
                  pb: 1,
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: colorTokens.grey[600],
                    display: 'block',
                  }}
                >
                  ※
                  会話の話者分割はバックグラウンドで処理されます。完了後、このタブの表示が自動的に更新されます。
                </Typography>
              </Box>
            )}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <TranscriptionViewer
                conversationSegments={dictatedData}
                showAudioPlayer={false}
                compact
              />
            </Box>
          </Box>
        )}

        {tabValue === TAB_INDICES.ACTION_PROPOSAL && (
          <Box
            sx={{
              height: '100%',
              overflow: 'auto',
              pr: 1,
            }}
          >
            {/* SOAP改善提案セクション */}
            {soapImprovementsLoading || isPolling ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <CircularProgress
                  size={24}
                  thickness={3}
                  sx={{
                    color: colorTokens.grey[500],
                  }}
                />
                <Typography sx={{ color: colorTokens.grey[500] }}>
                  改善提案を確認中...
                </Typography>
              </Box>
            ) : (
              <>
                {/* SOAP改善提案の表示 - 全カテゴリを常に表示 */}
                <Box sx={{ mb: 3 }}>
                  {groupedSoapImprovements.map((group) => (
                    <ActionCategorySection
                      key={group.id}
                      category={group}
                      isEmpty={group.suggestions.length === 0}
                    >
                      {group.suggestions.map((suggestion) => (
                        <SoapImprovementCard
                          key={suggestion.id}
                          suggestion={suggestion}
                          onExecute={handleSoapImprovementExecute}
                          onDismiss={handleSoapImprovementDismiss}
                        />
                      ))}
                    </ActionCategorySection>
                  ))}
                </Box>

                {/* フォローアップアクション */}
                {displayActions.length > 0 && (
                  <ActionCategorySection
                    category={{
                      id: 'followup',
                      label: 'フォローアップ',
                      icon: 'Event',
                      color: colorTokens.grey[800],
                      priority: 3,
                    }}
                    isEmpty={displayActions.length === 0}
                  >
                    {displayActions.map((action) => (
                      <ActionCardCompact
                        key={action.id}
                        action={action}
                        onStatusUpdate={onStatusUpdate}
                      />
                    ))}
                  </ActionCategorySection>
                )}
              </>
            )}
          </Box>
        )}

        {tabValue === TAB_INDICES.PRESCRIPTION_INFO && (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <PrescriptionInfoTab
              currentPrescription={
                patientDetail?.current_prescription_data || undefined
              }
              previousPrescription={
                patientDetail?.previous_prescription_data || undefined
              }
            />
          </Box>
        )}

        {tabValue === TAB_INDICES.GUIDANCE_SCRIPT && (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <GuidanceTabContent
              loading={guidanceLoading}
              data={guidanceData}
              type="script"
              emptyMessage="フレーズは取得されませんでした"
            />
          </Box>
        )}

        {tabValue === TAB_INDICES.GUIDANCE_DETAIL && (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <GuidanceTabContent
              loading={guidanceLoading}
              data={guidanceData}
              type="detail"
              emptyMessage="詳細は取得されませんでした"
            />
          </Box>
        )}
      </Box>
    </Paper>
  )
}

export default PatientActionProposal
